"use client";

import React, { useState, useMemo, useCallback, useEffect, Suspense } from "react";
import { useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useParams } from "next/navigation";
import TableCard from "@/components/TableCard";
import { useDispatch } from "react-redux";
import baseApi from "@/lib/redux/api/baseApi";
import Checkbox from "@/components/Checkbox";
import {
  useGetFormQuery,
  useSaveFormMutation,
  useGetSessionsByDateMutation,
} from "@/lib/redux/api/customerFormApi";
import { useViewInterestedInListForFormsQuery, useViewLocationListQuery } from "@/lib/redux/api/interestedInApi";
import InputField from "@/components/InputField";
import Button from "@/components/Button";
import PhoneInputField from "@/components/PhoneInputField";
import FileInputImages from "@/components/ImageUploader";
import Radio from "@/components/RadioButton";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import DropdownField from "@/components/DropdownField";
import { toast } from "react-toastify";
import SignatureField from "@/components/SignatureField";
import { useSearchParams } from "next/navigation";
import DeletePopup from "@/components/Popups/DeletePopup";
import SvgIcon from "@/lib/icons/SvgIcon";
import DataTable from "@/components/DataTable";
import { decryptData } from "@/lib/utils/encryption";
import Status from "@/components/Status";
import { Tooltip } from 'react-tooltip';
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addDays,
  isToday,
  isSameDay,
} from "date-fns";
import { useGetLogoQuery } from "@/lib/redux/api/globalSettingsApi";

const isSignatureField = (field) => {
  const slug = field?.slug?.toLowerCase() || "";
  const label = field?.label?.toLowerCase() || "";
  return (
    field?.field_type === "signature" ||
    slug.includes("signature") ||
    label.includes("signature")
  );
};

const parseJsonSafely = (value, fallback = null) => {
  if (!value) {
    return fallback;
  }

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
        return fallback;
  }
};

const PORTAL_PRIMARY_COLOR = "#883DF2";

const resolveFormPrimaryColor = (...candidates) => {
  for (const color of candidates) {
    if (!color || typeof color !== "string") continue;
    const trimmed = color.trim();
    if (!trimmed) continue;
    // Old create default was #000000 when no color was chosen
    if (/^#(?:000|000000)$/i.test(trimmed)) continue;
    return trimmed;
  }
  return PORTAL_PRIMARY_COLOR;
};

/** Convert #RGB / #RRGGBB to "r g b" for CSS rgb(var(--primary-rgb) / …) */
const hexToRgbChannels = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  let value = hex.trim().replace("#", "");
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
};

const FormCenteredLoader = ({ color }) => (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-30">
    <div className="bg-white rounded-xl shadow-lg p-10 flex items-center justify-center">
      <div
        className="animate-spin rounded-full h-20 w-20 border-b-4"
        style={{
          borderBottomColor: color || PORTAL_PRIMARY_COLOR,
        }}
      />
    </div>
  </div>
);

const pruneEmptyValues = (obj) => {
  if (!obj || typeof obj !== "object") {
    return {};
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value === null || value === undefined || value === "") {
      return acc;
    }

    if (typeof value === "object" && !Array.isArray(value)) {
      const nested = pruneEmptyValues(value);
      if (Object.keys(nested).length > 0) {
        acc[key] = nested;
      }
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
};

/**
 * Extracts the marketing/tracking URL param value from search params.
 * Ignores internal params like user_id, source, referral_code, etc.
 * Returns a single string (e.g. "test=2222" or "utm_source=fb&utm_campaign=spring").
 */
const getUrlParamValueFromSearchParams = (searchParams) => {
  if (!searchParams) return null;

  const ignoredKeys = new Set([
    "user_id",
    "source",
    "referral_code",
    "utm_source_internal",
  ]);

  const parts = [];

  for (const [key, value] of searchParams.entries()) {
    if (ignoredKeys.has(key)) continue;
    if (value == null || value === "") continue;
    parts.push(`${key}=${value}`);
  }

  if (parts.length === 0) {
    return null;
  }

  return parts.join("&");
};

const CustomerFormPage = () => {
  const params = useParams();

  const searchParams = useSearchParams();
  const encryptedUserId = decodeURIComponent(searchParams.get("user_id") || '').replace(/ /g, '+');
  const sourceParam = searchParams.get("source");
  const leadSource = sourceParam ? sourceParam : undefined;
  const referralCodeParam = searchParams.get("referral_code");
  const referralCode = referralCodeParam ? referralCodeParam : undefined;
  const embedParam = (searchParams.get("embed") || "").toLowerCase();
  const isEmbedQuery = embedParam === "1" || embedParam === "true";
  const themeColorParam = searchParams.get("theme_color");
  const userId = decryptData(encryptedUserId);
  const formId = params?.formId;
  // const [existingMedia, setExistingMedia] = useState([]);
  const [existingMedia] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isInIframe, setIsInIframe] = useState(false);
  const today = new Date().toISOString().split("T")[0];
  const [selectedBookingDate, setSelectedBookingDate] = useState(today);
  const [selectedSessionGuid, setSelectedSessionGuid] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    message: "",
    classData: null,
  });

  const [clientInfo, setClientInfo] = useState({
    ipAddress: null,
    userAgent: "",
    language: "",
    platform: "",
    screen: { width: null, height: null },
    timezone: "",
    referrer: "",
    currentUrl: "",
  });

  // State for dynamically added children (for client_registration forms)
  const [dynamicChildren, setDynamicChildren] = useState([]);

  const dispatch = useDispatch();
  const [isNavigating, setIsNavigating] = useState(false);
  const [saveForm, { isLoading: isSavingForm }] = useSaveFormMutation();
  const isSubmitting = isSavingForm || isNavigating;

  // const [selectedClass, setSelectedClass] = useState(null);

  const { data: formData, isLoading, isUninitialized } = useGetFormQuery(formId?.toString(), {
    refetchOnMountOrArgChange: true,
  });
  const formPrimaryColor = resolveFormPrimaryColor(
    themeColorParam,
    formData?.data?.title_color
  );
  const isClientRegistration = formData?.data?.form_type === "client_registration";

  const urlParamValueFromUrl = useMemo(
    () => getUrlParamValueFromSearchParams(searchParams),
    [searchParams]
  );

  const [
    getSessionsByDate,
    { data: sessionsByDateData, isLoading: sessionsByDateLoading },
  ] = useGetSessionsByDateMutation();

  // const [fetchedDates, setFetchedDates] = useState(new Set());

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Global logo (for all public forms) - scoped by userId from URL
  const { data: logoResponse } = useGetLogoQuery(userId, {
    skip: !userId,
  });
  const logoUrl = logoResponse?.data?.url || null;

  const handleSessionsByDate = useCallback(
    (date) => {
      if (userId && date && formId) {
        const weekdayName = format(new Date(date), "EEEE");

        getSessionsByDate({
          userId,
          date,
          day: weekdayName,
          formId,
        });

        // setFetchedDates((prev) => new Set([...prev, date]));
      }
    },
    [getSessionsByDate, userId, formId] // ✅ Include all dependencies
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let isMounted = true;

    const baseInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      referrer: document.referrer,
      currentUrl: window.location.href,
      screen: {
        width: window.screen?.width || null,
        height: window.screen?.height || null,
      },
    };

    if (isMounted) {
      setClientInfo((prev) => ({
        ...prev,
        ...baseInfo,
      }));
    }

    const fetchIp = async () => {
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        if (!response.ok) {
          throw new Error("Failed to fetch IP");
        }
        const data = await response.json();
        if (isMounted) {
          setClientInfo((prev) => ({
            ...prev,
            ipAddress: data?.ip || null,
          }));
        }
      } catch (error) {
                if (isMounted) {
          setClientInfo((prev) => ({
            ...prev,
            ipAddress: prev.ipAddress || null,
          }));
        }
      }
    };

    fetchIp();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (userId && formId && !initialDataLoaded) {
      handleSessionsByDate(today);
      setInitialDataLoaded(true);
    }
  }, [userId, formId, handleSessionsByDate, today, initialDataLoaded]);

  const { data: sessionsListingData, isLoading: sessionsLoading } =
    useViewInterestedInListForFormsQuery(formId, {
      refetchOnMountOrArgChange: true,
    });

  const { data: locationData } =
    useViewLocationListQuery(formId, {
      refetchOnMountOrArgChange: true,
    });
  
  // Helper function to fix slug based on label (handles backend data issues)
  const fixFieldSlug = (field) => {
    const labelLower = (field.label || "").toLowerCase();
    const currentBaseSlug = field.slug.replace(/^child_\d+_/, "");
    const childPrefix = field.slug.match(/^(child_\d+_)/)?.[1] || "child_1_";

    // Determine expected base slug from label
    let expectedBaseSlug = currentBaseSlug;
    if (labelLower.includes("first name") || labelLower === "first name" || labelLower.includes("first")) {
      expectedBaseSlug = "first_name";
    } else if (labelLower.includes("last name") || labelLower === "last name" || labelLower.includes("last")) {
      expectedBaseSlug = "last_name";
    } else if (labelLower.includes("dob") || labelLower.includes("date of birth") || labelLower.includes("birth") || labelLower.includes("date")) {
      expectedBaseSlug = "dob";
    }

    // If slug doesn't match expected, fix it
    if (currentBaseSlug !== expectedBaseSlug) {
      return {
        ...field,
        slug: `${childPrefix}${expectedBaseSlug}`,
      };
    }
    return field;
  };

  // Helper function to group child fields (must be defined before useMemo hooks that use it)
  const groupChildFields = (formQuestions) => {
    const childGroups = {};

    formQuestions?.forEach((field) => {
      if (field?.is_active === 0) {
        return; // skip inactive
      }
      if (field.field_category?.startsWith("child_")) {
        const childNumber = field.field_category;
        if (!childGroups[childNumber]) {
          childGroups[childNumber] = [];
        }
        // Fix slug before adding to group
        const fixedField = fixFieldSlug(field);
        childGroups[childNumber].push(fixedField);
      }
    });

    // Remove duplicates within each group by ensuring unique slugs
    Object.keys(childGroups).forEach(childNumber => {
      const slugMap = new Map();
      const uniqueFields = [];
      childGroups[childNumber].forEach((field, index) => {
        if (slugMap.has(field.slug)) {
          // Generate unique slug from label
          const labelSlug = (field.label || "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "") || `field_${field.id || index}`;
          const childPrefix = field.slug.match(/^(child_\d+_)/)?.[1] || `${childNumber}_`;
          const newSlug = `${childPrefix}${labelSlug}`;

          if (!slugMap.has(newSlug)) {
            slugMap.set(newSlug, true);
            uniqueFields.push({
              ...field,
              slug: newSlug,
            });
          }
        } else {
          slugMap.set(field.slug, true);
          uniqueFields.push(field);
        }
      });
      childGroups[childNumber] = uniqueFields;
    });

    return childGroups;
  };

  // Get child field template from the first child group (child_1)
  // This must be called as a hook consistently, right after query hooks
  const getChildFieldTemplate = useMemo(() => {
    if (!formData?.data?.form_question) {
      return [];
    }
    const childGroups = groupChildFields(formData.data.form_question);
    const firstChildGroup = childGroups["child_1"] || [];

    // Sort template fields to ensure consistent order (first_name, last_name, dob)
    let sortedFields = firstChildGroup
      .filter((field) => !isSignatureField(field))
      .sort((a, b) => {
        const order = ["first_name", "last_name", "dob"];
        // Extract the base field name (e.g., "first_name" from "child_1_first_name")
        const aBase = a.slug.replace(/^child_\d+_/, "");
        const bBase = b.slug.replace(/^child_\d+_/, "");
        return order.indexOf(aBase) - order.indexOf(bBase);
      });

    // Fix duplicate or incorrect slugs based on labels
    // This handles backend data issues where slugs don't match labels or are duplicated
    // Step 1: First pass - fix slugs based on labels
    const labelFixedFields = sortedFields.map((field) => {
      const labelLower = (field.label || "").toLowerCase();
      const currentBaseSlug = field.slug.replace(/^child_\d+_/, "");

      // Determine expected base slug from label
      let expectedBaseSlug = currentBaseSlug;
      if (labelLower.includes("first name") || labelLower === "first name" || labelLower.includes("first")) {
        expectedBaseSlug = "first_name";
      } else if (labelLower.includes("last name") || labelLower === "last name" || labelLower.includes("last")) {
        expectedBaseSlug = "last_name";
      } else if (labelLower.includes("dob") || labelLower.includes("date of birth") || labelLower.includes("birth") || labelLower.includes("date")) {
        expectedBaseSlug = "dob";
      }

      // If slug doesn't match expected, fix it
      if (currentBaseSlug !== expectedBaseSlug) {
        return {
          ...field,
          slug: `child_1_${expectedBaseSlug}`,
        };
      }
      return { ...field };
    });

    // Step 2: Remove duplicates by ensuring each field has a unique slug
    const slugMap = new Map();
    const uniqueFields = labelFixedFields.map((field, index) => {
      // If this slug is already used, generate a unique one
      if (slugMap.has(field.slug)) {
        // Generate slug from label (e.g., "Last Name" -> "last_name")
        const labelSlug = (field.label || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "") || `field_${field.id || index}`;
        const newSlug = `child_1_${labelSlug}`;

        // If the new slug is also taken, add index to make it unique
        if (slugMap.has(newSlug)) {
          const uniqueSlug = `child_1_${labelSlug}_${index}`;
          slugMap.set(uniqueSlug, true);
          return {
            ...field,
            slug: uniqueSlug,
          };
        }

        slugMap.set(newSlug, true);
        return {
          ...field,
          slug: newSlug,
        };
      }

      slugMap.set(field.slug, true);
      return field;
    });

    return uniqueFields;
  }, [formData?.data?.form_question]);

  const modifiedSessionData = sessionsListingData?.data
    ?.slice() // clone to avoid mutating original
    ?.sort((a, b) => a.position - b.position)
    ?.map((session) => ({
      label: session.name,
      value: session.id,
    }));

  const modifiedLocationData = locationData?.data?.map((loc) => ({
    label: loc?.location_name,
    value: loc?.guid,
  }));
  const allClasses = useMemo(() => {
    if (!sessionsByDateData?.classes) {
      return [];
    }

    const now = new Date();
    const today = format(now, "yyyy-MM-dd");

    const formattedSelectedDate = format(selectedDate, "yyyy-MM-dd");

    return Object.values(sessionsByDateData.classes).map((classItem) => {
      const {
        name,
        starttime,
        endtime,
        startdate,
        enddate,
        instructor,
        session_capacity,
        session_booking_count,
        guid,
      } = classItem;

      const formattedStartTime = starttime
        ? new Date(`2000-01-01T${starttime}`).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        : "N/A";

      const formattedEndTime = endtime
        ? new Date(`2000-01-01T${endtime}`).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        : "N/A";

      let isPast = false;

      if (formattedSelectedDate === today) {
        if (starttime) {
          const [hours, minutes] = starttime.split(":");
          const sessionTime = new Date();
          sessionTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

          isPast = now > sessionTime;
        }
      } else {
        isPast = formattedSelectedDate < today;

        if (enddate && formattedSelectedDate > enddate) {
          isPast = true;
        }

        if (startdate && formattedSelectedDate < startdate) {
          isPast = false;
        }
      }

      return {
        session_name: name,
        session_time: `${formattedStartTime} - ${formattedEndTime}`,
        session_date: startdate,
        instructor,
        session_capacity,
        session_booking_count,
        guid,
        is_past: isPast,
        end_date: enddate,
      };
    });
  }, [sessionsByDateData?.classes, selectedDate]);

  const getDaysInWeek = () => {
    const start = startOfWeek(currentDate);
    const end = endOfWeek(currentDate);
    return eachDayOfInterval({ start, end });
  };

  const handlePreviousWeek = () => {
    setCurrentDate(addDays(currentDate, -7));
  };

  const handleNextWeek = () => {
    setCurrentDate(addDays(currentDate, 7));
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSelectedBookingDate(date);
    const formattedDate = format(date, "yyyy-MM-dd");
    if (userId && formId) {
      handleSessionsByDate(formattedDate); // ✅ Pass only the date
    }
  };

  // Helper function to generate validation rule for a field
  const generateFieldValidation = (field) => {
    if (field?.is_active === 0) {
      return null;
    }
    const signatureField = isSignatureField(field);
    if (signatureField) {
      return field?.is_required
        ? Yup.string()
          .trim()
          .required(`${field.label} is required`)
        : Yup.string().trim().nullable();
    }

    if (field?.is_required) {
      switch (field?.field_type) {
        case "text":
          return Yup.string()
            .trim()
            .required(`${field.label} is required`)
            .max(255, `${field.label} must not exceed 255 characters`);
        case "email":
          return Yup.string()
            .trim()
            .required(`${field.label} is required`)
            .email(`Please enter a valid email address`)
            .max(255, `${field.label} must not exceed 255 characters`);
        case "phone":
          return Yup.string()
            .trim()
            .required("Phone number is required")
            .test("validPhone", "Invalid phone number", (value) =>
              value ? isValidPhoneNumber(value) : false
            );
        case "radio":
          return Yup.string().required(
            `Please select a ${field.label.toLowerCase()}`
          );
        case "select": {
          const isMultiSelect = field?.is_multi_select || field?.multiple || field?.slug === "interested_in";
          if (isMultiSelect) {
            return Yup.array()
              .min(1, `Please select at least one ${field.label.toLowerCase()}`)
              .required(`Please select at least one ${field.label.toLowerCase()}`);
          } else {
            return Yup.string().required(
              `Please select a ${field.label.toLowerCase()}`
            );
          }
        }
        case "checkbox":
          return Yup.array()
            .transform((value) => (value && value.length > 0 ? value : []))
            .min(1, `Please select at least one ${field.label.toLowerCase()}`)
            .required(
              `Please select at least one ${field.label.toLowerCase()}`
            );
        case "date": {
          const isDobField = field.slug.toLowerCase().includes('dob') ||
            field.slug.toLowerCase().includes('date_of_birth') ||
            field.label.toLowerCase().includes('dob') ||
            field.label.toLowerCase().includes('date of birth');
          let dateSchema = Yup.date()
            .typeError("Invalid date format")
            .required(`${field.label} is required`);
          if (isDobField) {
            dateSchema = dateSchema.max(
              new Date(),
              `${field.label} cannot be a future date`
            );
          }
          return dateSchema;
        }
        case "file":
          return Yup.mixed().test(
            "fileSize",
            "File size is too large",
            (value) => {
              if (!value || !value.newFiles?.length) {
                return true;
              }
              const file = value.newFiles[0];
              return file?.size <= 5000000;
            }
          );
        case "textarea":
          return Yup.string()
            .trim()
            .required(`${field.label} is required`)
            .max(1000, `${field.label} must not exceed 1000 characters`);
        default:
          return Yup.string()
            .trim()
            .required(`${field.label} is required`);
      }
    } else {
      switch (field?.field_type) {
        case "email":
          return Yup.string()
            .trim()
            .email(`Please enter a valid email address`)
            .max(255)
            .nullable();
        case "phone":
          return Yup.string()
            .trim()
            .matches(
              /^[0-9]{10}$/,
              "Please enter a valid 10-digit phone number"
            )
            .nullable();
        case "select": {
          const isMultiSelect = field?.is_multi_select || field?.multiple || field?.slug === "interested_in";
          if (isMultiSelect) {
            return Yup.array()
              .transform((value) => {
                if (!value || (Array.isArray(value) && value.length === 0)) {
                  return undefined;
                }
                return value;
              })
              .nullable();
          } else {
            return Yup.string().nullable();
          }
        }
        case "file":
          return Yup.mixed().test(
            "fileSize",
            "File size is too large",
            (value) => {
              if (!value || !value.newFiles?.length) {
                return true;
              }
              const file = value.newFiles[0];
              return file?.size <= 5000000;
            }
          );
        case "checkbox":
          return Yup.array()
            .transform((value) => (value && value.length > 0 ? value : []))
            .nullable();
        case "date": {
          const isDobField = field.slug.toLowerCase().includes('dob') ||
            field.slug.toLowerCase().includes('date_of_birth') ||
            field.label.toLowerCase().includes('dob') ||
            field.label.toLowerCase().includes('date of birth');
          let dateSchema = Yup.date()
            .typeError("Invalid date format")
            .nullable();
          if (isDobField) {
            dateSchema = dateSchema.max(
              new Date(),
              `${field.label} cannot be a future date`
            );
          }
          return dateSchema;
        }
        default:
          return Yup.string().trim().nullable();
      }
    }
  };

  const validationSchema = React.useMemo(() => {
    if (!formData?.data?.form_question) {
      return {};
    }
    const schema = {};
    formData.data.form_question.forEach((field) => {
      const validation = generateFieldValidation(field);
      if (validation) {
        schema[field.slug] = validation;
      }
    });

    // Add validation for dynamically added child fields
    if (isClientRegistration && getChildFieldTemplate.length > 0) {
      dynamicChildren.forEach((childNumber) => {
        // Process template fields in the same order as they're rendered
        getChildFieldTemplate.forEach((templateField) => {
          // Extract the base slug (e.g., "first_name" from "child_1_first_name")
          const baseSlug = templateField.slug.replace(/^child_\d+_/, "");
          const dynamicSlug = `child_${childNumber}_${baseSlug}`;

          // Create dynamic field with correct slug and preserve all other properties including label
          // IMPORTANT: Create a new object to avoid reference issues and ensure correct field identity
          const dynamicField = {
            ...templateField,
            slug: dynamicSlug,
            field_category: `child_${childNumber}`,
            // Explicitly preserve label to ensure it matches the field type
            label: templateField.label,
            // Ensure field_type is preserved
            field_type: templateField.field_type,
            // Ensure is_required is preserved
            is_required: templateField.is_required,
          };

          // Only create validation if the field is active
          if (dynamicField.is_active !== 0) {
            const validation = generateFieldValidation(dynamicField);
            if (validation) {
              // Use the dynamic slug as the schema key to match the field name in Controller
              // This ensures errors are associated with the correct field
              schema[dynamicSlug] = validation;
            }
          }
        });
      });
    }

    return schema;
  }, [formData?.data?.form_question, isClientRegistration, dynamicChildren, getChildFieldTemplate]);

  const formOptions = React.useMemo(
    () => ({
      resolver: yupResolver(Yup.object().shape(validationSchema)),
      mode: "onChange",
      defaultValues: {
        "terms_&_conditions": true,
        // Add default values for multi-select fields
        ...formData?.data?.form_question?.reduce((defaults, field) => {
          if (field?.is_active === 0) {
            return defaults; // Skip inactive fields from defaults
          }
          const isMultiSelect = field?.is_multi_select || field?.multiple || field?.slug === "interested_in";
          if (field?.field_type === "select" && isMultiSelect) {
            defaults[field.slug] = [];
          }
          return defaults;
        }, {})
      },
    }),
    [validationSchema, formData?.data?.form_question]
  );

  const {
    register,
    control,
    setValue,
    watch,
    clearErrors,
    getValues,
    trigger,
    setError,
    formState: { errors },
  } = useForm(formOptions);

  // Store refs to signature canvas components for persistence before submission
  const signatureRefs = React.useRef({});

  // Function to add a new child (must be before early returns)
  const handleAddChild = useCallback(() => {
    // Find the highest existing child number from form data
    const existingChildGroups = groupChildFields(formData?.data?.form_question || []);
    const existingChildNumbers = Object.keys(existingChildGroups)
      .map(key => {
        const match = key.match(/child_(\d+)/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(num => num > 0);
    const maxExistingChild = existingChildNumbers.length > 0 ? Math.max(...existingChildNumbers) : 0;
    // Find the highest dynamic child number
    const maxDynamicChild = dynamicChildren.length > 0 ? Math.max(...dynamicChildren) : 0;
    // New child number should be max of existing + dynamic + 1
    const newChildNumber = Math.max(maxExistingChild, maxDynamicChild, 0) + 1;

    setDynamicChildren(prev => [...prev, newChildNumber]);
  }, [dynamicChildren, formData?.data?.form_question]);

  // Function to remove a child (must be before early returns)
  const handleRemoveChild = useCallback((childNumber) => {
    setDynamicChildren(prev => prev.filter(num => num !== childNumber));
    // Clear form values for this child's fields
    if (getChildFieldTemplate.length > 0) {
      getChildFieldTemplate.forEach(templateField => {
        const fieldSlug = templateField.slug.replace(/^child_\d+_/, `child_${childNumber}_`);
        setValue(fieldSlug, "");
        clearErrors(fieldSlug);
      });
    }
  }, [getChildFieldTemplate, setValue, clearErrors]);

  // Create dynamic child fields from template (must be before early returns)
  const createDynamicChildFields = useCallback((childNumber) => {
    if (!getChildFieldTemplate || getChildFieldTemplate.length === 0) {
      return [];
    }
    return getChildFieldTemplate.map(templateField => {
      // Extract the base slug (e.g., "first_name" from "child_1_first_name")
      const baseSlug = templateField.slug.replace(/^child_\d+_/, "");
      const newSlug = `child_${childNumber}_${baseSlug}`;
      const newFieldCategory = `child_${childNumber}`;

      return {
        ...templateField,
        slug: newSlug,
        field_category: newFieldCategory,
        // Create a new ID to avoid conflicts (use negative numbers for dynamic fields)
        id: templateField.id ? -(childNumber * 1000 + templateField.id) : undefined,
      };
    });
  }, [getChildFieldTemplate]);

  const buildSignatureMetadataPayload = useCallback(
    (rawMetadata) => {
      const parsedMetadata = parseJsonSafely(rawMetadata, {}) || {};
      const sanitizedClientInfo = pruneEmptyValues(clientInfo);

      // Ensure we have all browser information
      const browserInfo = {
        userAgent: sanitizedClientInfo.userAgent || navigator.userAgent,
        language: sanitizedClientInfo.language || navigator.language,
        platform: sanitizedClientInfo.platform || navigator.platform,
        screen: sanitizedClientInfo.screen || {
          width: window.screen?.width || null,
          height: window.screen?.height || null,
        },
        timezone: sanitizedClientInfo.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const parsedBrowser = parsedMetadata.browser || {};
      const parsedClientEnvironment = parsedMetadata.clientEnvironment || {};

      // Build comprehensive metadata with all browser info and IP
      const mergedMetadata = {
        // Preserve existing metadata from signature field
        ...parsedMetadata,
        // Ensure IP address is included (from clientInfo or parsed metadata)
        ipAddress: sanitizedClientInfo.ipAddress || parsedMetadata.ipAddress || null,
        // Ensure referrer is included
        referrer: sanitizedClientInfo.referrer || parsedMetadata.referrer || document.referrer || null,
        // Ensure current URL is included
        currentUrl: sanitizedClientInfo.currentUrl || parsedMetadata.currentUrl || window.location.href || null,
        // Merge browser information
        browser: {
          ...parsedBrowser,
          ...browserInfo,
        },
        // Merge client environment information
        clientEnvironment: {
          ...parsedClientEnvironment,
          ...sanitizedClientInfo,
        },
        // Ensure signature timestamp is included
        signatureCapturedAt: parsedMetadata.signatureCapturedAt || parsedMetadata.signedAt || new Date().toISOString(),
        // Add submission timestamp
        submittedAt: new Date().toISOString(),
      };

      // Remove any null/undefined values but keep empty objects/arrays
      const cleanedMetadata = Object.fromEntries(
        Object.entries(mergedMetadata).filter(([_, v]) => v !== null && v !== undefined)
      );

      if (!Object.keys(cleanedMetadata).length) {
        return null;
      }

      return JSON.stringify(cleanedMetadata);
    },
    [clientInfo]
  );

  const transformFormData = (formValues) => {

    const formDataObj = new FormData();
    let index = 0;
    const formType = formData?.data?.form_type || "";

    formData?.data?.form_question?.forEach((field) => {
      if (field?.is_active === 0) {
        return; // Do not submit inactive fields
      }
      const prefix = `answers[${index}]`;
      const value = formValues[field.slug];

      const metadataValue = formValues[`${field.slug}_metadata`];

      // ⬅ Signature field handling
      if (isSignatureField(field)) {
        // Get signature from ref if available (in case form value is not updated yet)
        let signatureValue = value;
        const sigRef = signatureRefs.current[field.slug];

        // Always try to get the latest signature from the canvas
        if (sigRef) {
          try {
            // Check if signature exists
            const isEmpty = sigRef.isEmpty();

            if (!isEmpty) {
              const dataUrl = sigRef.getDataUrl();
              if (dataUrl && dataUrl.startsWith('data:image')) {
                signatureValue = dataUrl;
                // Update form value to ensure it's captured
                if (signatureValue !== value) {
                  setValue(field.slug, signatureValue, { shouldValidate: true, shouldDirty: true });
                }
              }
            }
          } catch (error) {
                        // If we have a value in form, use it
            if (!signatureValue || signatureValue.trim() === "") {
                            return;
            }
          }
        } else {
                  }

        // Only include signature if it has a valid data URL value
        if (!signatureValue ||
          (typeof signatureValue === 'string' &&
            (signatureValue.trim() === "" || !signatureValue.startsWith('data:image')))) {
                    return;
        }
        // Always include signature as a separate answer (same as other fields)
        formDataObj.append(`${prefix}[question_id]`, field.id);
        formDataObj.append(`${prefix}[label]`, field.label || "signature");
        formDataObj.append(`${prefix}[slug]`, field.slug);
        formDataObj.append(`${prefix}[answer]`, signatureValue);
        formDataObj.append(`${prefix}[field_category]`, field.field_category || "lead");

        // Build comprehensive metadata with ONLY browser info and IP (not client_more_info)
        // This metadata will be stored in the signature answer's metadata field
        let signatureMetadata = buildSignatureMetadataPayload(metadataValue);

        // If no metadata was created, build a fresh one with current browser info
        if (!signatureMetadata) {
          // Create metadata directly with current clientInfo (browser info and IP only)
          const freshMetadata = {
            ipAddress: clientInfo.ipAddress || null,
            userAgent: clientInfo.userAgent || navigator.userAgent,
            language: clientInfo.language || navigator.language,
            platform: clientInfo.platform || navigator.platform,
            screen: clientInfo.screen || {
              width: window.screen?.width || null,
              height: window.screen?.height || null,
            },
            timezone: clientInfo.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            referrer: clientInfo.referrer || document.referrer || null,
            currentUrl: clientInfo.currentUrl || window.location.href,
            signatureCapturedAt: new Date().toISOString(),
            submittedAt: new Date().toISOString(),
          };

          // Remove null values
          const cleanedMetadata = Object.fromEntries(
            Object.entries(freshMetadata).filter(([_, v]) => v !== null && v !== undefined)
          );

          signatureMetadata = JSON.stringify(cleanedMetadata);
        }

        // Always append metadata for signature fields (browser info and IP)
        if (signatureMetadata) {
          formDataObj.append(`${prefix}[metadata]`, signatureMetadata);
        }

        index++;
        return;
      }

      if (field.field_type === "file") {
        const files = value?.newFiles;
        if (files && files.length > 0) {
          const file = files[0];
          formDataObj.append(`${prefix}[question_id]`, field.id);
          formDataObj.append(`${prefix}[label]`, 'file'); // Laravel condition checks this
          formDataObj.append(`${prefix}[slug]`, field.slug);
          formDataObj.append(`${prefix}[answer]`, file, file.name); // ✅ Send actual file with name
          formDataObj.append(`${prefix}[field_category]`, field.field_category);
          index++;
        }
      } else if (value) {
        formDataObj.append(`${prefix}[question_id]`, field.id);
        formDataObj.append(`${prefix}[label]`, field.label);
        formDataObj.append(`${prefix}[slug]`, field.slug);
        if (field.field_type) {
          formDataObj.append(`${prefix}[field_type]`, field.field_type);
        }
        if (Array.isArray(value)) {
          formDataObj.append(`${prefix}[answer]`, JSON.stringify(value));
        } else {
          formDataObj.append(`${prefix}[answer]`, value);
        }
        formDataObj.append(`${prefix}[field_category]`, field.field_category);
        index++;
      }
    });

    // Process dynamically added child fields (for client_registration)
    if (isClientRegistration && getChildFieldTemplate.length > 0) {
      dynamicChildren.forEach((childNumber) => {
        getChildFieldTemplate.forEach((templateField) => {
          if (templateField?.is_active === 0) {
            return; // Skip inactive fields
          }
          const baseSlug = templateField.slug.replace(/^child_\d+_/, "");
          const dynamicSlug = `child_${childNumber}_${baseSlug}`;
          const prefix = `answers[${index}]`;
          const value = formValues[dynamicSlug];

          if (templateField.field_type === "file") {
            const files = value?.newFiles;
            if (files && files.length > 0) {
              const file = files[0];
              formDataObj.append(`${prefix}[question_id]`, templateField.id || "");
              formDataObj.append(`${prefix}[label]`, 'file');
              formDataObj.append(`${prefix}[slug]`, dynamicSlug);
              formDataObj.append(`${prefix}[answer]`, file, file.name);
              formDataObj.append(`${prefix}[field_category]`, `child_${childNumber}`);
              index++;
            }
          } else if (value) {
            formDataObj.append(`${prefix}[question_id]`, templateField.id || "");
            formDataObj.append(`${prefix}[label]`, templateField.label);
            formDataObj.append(`${prefix}[slug]`, dynamicSlug);
            if (Array.isArray(value)) {
              formDataObj.append(`${prefix}[answer]`, JSON.stringify(value));
            } else {
              formDataObj.append(`${prefix}[answer]`, value);
            }
            formDataObj.append(`${prefix}[field_category]`, `child_${childNumber}`);
            index++;
          }
        });
      });
    }

    formDataObj.append("form_guid", formData?.data?.guid || "");
    if (formValues?.["terms_&_conditions"]) {
      formDataObj.append("terms_&_conditions", "1");
    }

    // Only append leadSource if it exists
    if (leadSource) {
      formDataObj.append("leadSource", leadSource);
    }

    // Only append referralCode if it exists
    if (referralCode) {
      formDataObj.append("referral_code", referralCode);
    }

    // URL attribution: raw URL param value + full source page URL
    if (urlParamValueFromUrl) {
      formDataObj.append("url_param_value", urlParamValueFromUrl);
    }
    if (typeof window !== "undefined") {
      formDataObj.append("source_page_url", window.location.href);
    }

    if (
      formType === "class_registration") {
      let dateObj;

      // Check if it's already a Date object
      if (selectedBookingDate instanceof Date) {
        dateObj = selectedBookingDate;
      } else {
        // If it's a string, safely parse it
        dateObj = new Date(selectedBookingDate);
      }

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');

      const formattedSessionDate = `${year}-${month}-${day}`;

      formDataObj.append("booking_date", formattedSessionDate);
      formDataObj.append("session_guid", selectedSessionGuid);

    }

    return formDataObj;
  };

  // Function to persist all signatures before validation
  const persistAllSignatures = useCallback(async () => {
    const signatureUpdates = [];

    Object.entries(signatureRefs.current).forEach(([fieldSlug, sigRef]) => {
      if (sigRef) {
        try {
          // First try to persist the signature if it exists
          if (typeof sigRef.persist === 'function') {
            sigRef.persist();
          }

          // Check if signature exists
          const isEmpty = sigRef.isEmpty();
          if (!isEmpty) {
            // Try to get data URL with multiple fallback methods
            let dataUrl = null;
            try {
              dataUrl = sigRef.getDataUrl();
            } catch (getUrlError) {
                            // Try alternative: get from form value if available
              const formValue = getValues(fieldSlug);
              if (formValue && formValue.startsWith('data:image')) {
                dataUrl = formValue;
              }
            }

            if (dataUrl && dataUrl.startsWith('data:image')) {
              signatureUpdates.push({
                fieldSlug,
                dataUrl,
                metadataFieldName: `${fieldSlug}_metadata`
              });
            } else {
                          }
          }
        } catch (error) {
                    // Try to use existing form value as fallback
          const formValue = getValues(fieldSlug);
          if (formValue && formValue.startsWith('data:image')) {
            signatureUpdates.push({
              fieldSlug,
              dataUrl: formValue,
              metadataFieldName: `${fieldSlug}_metadata`
            });
          }
        }
      }
    });

    // Update all signatures in form
    signatureUpdates.forEach(({ fieldSlug, dataUrl, metadataFieldName }) => {
      setValue(fieldSlug, dataUrl, { shouldValidate: true, shouldDirty: true });

      // Ensure metadata is set
      const existingMetadata = getValues(metadataFieldName);
      if (!existingMetadata) {
        const metadata = buildSignatureMetadataPayload(null);
        if (metadata) {
          setValue(metadataFieldName, metadata, { shouldDirty: true });
        }
      }
    });

    // Small delay to ensure form state is updated
    await new Promise(resolve => setTimeout(resolve, 100));

    // Trigger validation for all signature fields to ensure they're validated with latest values
    const signatureFields = Object.keys(signatureRefs.current);
    if (signatureFields.length > 0) {
      await Promise.all(signatureFields.map(fieldSlug => trigger(fieldSlug)));
    }
  }, [getValues, setValue, trigger, buildSignatureMetadataPayload]);

  // Custom submit handler that persists signatures before validation
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    await persistAllSignatures();

    const isValid = await trigger();

    // Only proceed if validation passes
    if (isValid) {
      // Get the latest form values
      const formData = getValues();
      // Call onSubmit with validated data
      await onSubmit(formData);
    } else {
          }
  };

  const onSubmit = async (data) => {
    const finalData = getValues();
    const transformedData = transformFormData(finalData);
    let shouldKeepNavigating = false;

    setIsNavigating(true);

    try {
      const response = await saveForm(transformedData).unwrap();

      if (response?.success) {
        dispatch(baseApi.util.invalidateTags(["Forms", "Customer Form"]));
        setSelectedBookingDate(null);
        setSelectedSessionGuid(null);

        const firstName = String(
          response.first_name || finalData.first_name || ""
        ).trim();
        const lastName = String(
          response.last_name || finalData.last_name || ""
        ).trim();
        const customerName = String(
          response.name || `${firstName} ${lastName}`.trim() || ""
        ).trim();
        const customerEmail = String(
          response.email || finalData.email || ""
        ).trim();
        const customerPhone = String(
          response.phone ||
            finalData.phone ||
            finalData.phone_number ||
            finalData.mobile ||
            Object.entries(finalData || {}).find(([key, value]) =>
              /phone|mobile/i.test(key) && typeof value === "string" && value.trim()
            )?.[1] ||
            ""
        ).trim();
        const registrationId =
          response.registration_id || response.registrationId || undefined;
        const bookingToken =
          response.booking_token || response.bookingToken || undefined;
        const location = response.location || undefined;
        const schedule = response.schedule || undefined;

        // Notify parent (Gymboree) — prefer encrypted booking token
        if (typeof window !== "undefined" && window.self !== window.top) {
          window.parent.postMessage(
            {
              type: "formSubmitted",
              bookingToken: bookingToken || undefined,
              booking_token: bookingToken || undefined,
              registrationId,
              location,
              schedule,
              email: customerEmail || undefined,
              phone: customerPhone || undefined,
              name: customerName || undefined,
              first_name: firstName || undefined,
              last_name: lastName || undefined,
              customerEmail: customerEmail || undefined,
              customerPhone: customerPhone || undefined,
              customerName: customerName || undefined,
              success_redirect_url:
                response.success_redirect_url || undefined,
              requires_payment: Boolean(response.requires_payment),
            },
            "*"
          );
          // Booking parent navigates to /booking/payment.
          // Contact/franchise parent navigates via success_redirect_url.
          return;
        }

        shouldKeepNavigating = true;

        let redirectUrl =
          response.success_redirect_url ||
          `/customer-form/success?message=${encodeURIComponent(
            response.message || "Form submitted successfully."
          )}&color=${encodeURIComponent(primaryColor || titleColor || "")}`;

        // Prefer encrypted booking token on the payment redirect URL
        try {
          const isAbsolute = /^https?:\/\//i.test(redirectUrl);
          const url = isAbsolute
            ? new URL(redirectUrl)
            : new URL(redirectUrl, window.location.origin);
          // CRM form success URL is often /thank-you — payment flow is /booking/payment
          if (bookingToken && /\/thank-you\/?$/i.test(url.pathname)) {
            url.pathname = url.pathname.replace(/\/thank-you\/?$/i, "/booking/payment");
          }
          if (bookingToken) {
            url.search = "";
            url.searchParams.set("booking", bookingToken);
            if (location) url.searchParams.set("location", location);
            if (schedule) url.searchParams.set("schedule", schedule);
          } else {
            if (registrationId) url.searchParams.set("registrationId", registrationId);
            if (location) url.searchParams.set("location", location);
            if (schedule) url.searchParams.set("schedule", schedule);
            if (customerEmail) url.searchParams.set("email", customerEmail);
            if (customerName) url.searchParams.set("name", customerName);
            if (customerPhone) url.searchParams.set("phone", customerPhone);
          }
          redirectUrl = isAbsolute
            ? url.toString()
            : `${url.pathname}${url.search}${url.hash}`;
        } catch (_) {
          /* keep original redirectUrl */
        }

        window.location.href = redirectUrl;
        return;
      }

      toast.error(response?.message || "Failed to submit form. Please try again.");
    } catch (error) {
      
      if (error?.status === 400 && error?.data?.errors) {
        const serverErrors = error.data.errors;

        Object.keys(serverErrors).forEach((field) => {
          const message = serverErrors[field][0];
          setError(field, { type: "server", message });
        });

        return; // Stop further processing if validation errors exist
      }

      if (error?.status === 403) {
        toast.error(
          error?.data?.message ||
          "You have already booked a session using this email. Please contact the admin to assign multiple packages and classes."
        );
      } else {
        toast.error(
          error?.data?.message || "Failed to submit form. Please try again."
        );
      }
    } finally {
      if (!shouldKeepNavigating) {
        setIsNavigating(false);
      }
    }
  };

  // Auto-resize iframe when embedded - MUST be before any early returns
  // This hook must always be called in the same order, regardless of loading state
  useEffect(() => {
    const inFrame = typeof window !== "undefined" && window.self !== window.top;
    setIsInIframe(inFrame);

    const sendHeightToParent = () => {
      if (window.self !== window.top) {
        // Prefer content root height so parent iframe does not include empty page padding
        const root =
          document.querySelector("[data-form-embed-root]") ||
          document.body;
        const height = Math.max(
          root?.scrollHeight || 0,
          root?.offsetHeight || 0,
          document.documentElement?.scrollHeight || 0
        );
        window.parent.postMessage(
          { type: "resize", height },
          "*"
        );
      }
    };

    // Send height on load and resize
    sendHeightToParent();
    window.addEventListener("resize", sendHeightToParent);

    // Use MutationObserver to detect content changes
    const observer = new MutationObserver(sendHeightToParent);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      window.removeEventListener("resize", sendHeightToParent);
      observer.disconnect();
    };
  }, [formData]); // Only depend on formData to avoid hook order issues

  // Match body/html to form background_color (or white) so gray body never bleeds through
  useEffect(() => {
    if (typeof document === "undefined") return;

    const bg = formData?.data?.background_color || "#ffffff";
    const accent = resolveFormPrimaryColor(
      themeColorParam,
      formData?.data?.title_color
    );

    const root = document.documentElement;
    const body = document.body;
    const prevHtmlBg = root.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    const prevPrimary = root.style.getPropertyValue("--primary");
    const prevPrimaryColor = root.style.getPropertyValue("--primary-color");
    const prevPrimaryRgb = root.style.getPropertyValue("--primary-rgb");

    root.style.backgroundColor = bg;
    body.style.backgroundColor = bg;

    root.style.setProperty("--primary", accent);
    root.style.setProperty("--primary-color", accent);
    const rgb = hexToRgbChannels(accent);
    if (rgb) root.style.setProperty("--primary-rgb", rgb);

    return () => {
      root.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
      if (prevPrimary) root.style.setProperty("--primary", prevPrimary);
      else root.style.removeProperty("--primary");
      if (prevPrimaryColor) root.style.setProperty("--primary-color", prevPrimaryColor);
      else root.style.removeProperty("--primary-color");
      if (prevPrimaryRgb) root.style.setProperty("--primary-rgb", prevPrimaryRgb);
      else root.style.removeProperty("--primary-rgb");
    };
  }, [
    formData?.data?.background_color,
    formData?.data?.title_color,
    themeColorParam,
  ]);

  if (isUninitialized || isLoading || sessionsLoading) {
    return <FormCenteredLoader color={formPrimaryColor} />;
  }

  const handleBookNow = (eventData) => {
    // setSelectedBookingDate(eventData.session_date);
    setSelectedSessionGuid(eventData.guid);
    setShowForm(true);
  };

  const handleBookNowClick = (classItem) => {
    const { session_capacity, session_booking_count } = classItem;

    setConfirmModal({
      isOpen: true,
      message:
        parseInt(session_capacity) > parseInt(session_booking_count)
          ? "Do you want to process this booking?"
          : "The session is fully booked. Do you want to continue with waitlist booking?",
      classData: classItem,
    });
  };

  const handleConfirmBooking = () => {
    if (confirmModal.classData) {
      handleBookNow(confirmModal.classData);
    }
    setConfirmModal({ isOpen: false, message: "", classData: null });
  };

  const columns = (onBookNow) => [
    { name: "Name", selector: (row) => row.session_name, sortable: true },
    {
      name: "Time",
      selector: (row) => row.session_time || "-",
      sortable: true,
    },
    {
      name: "Instructor",
      selector: (row) =>
        row.instructor?.length ? row.instructor[0].name : "-",
      sortable: true,
    },
    {
      name: "Openings",
      width: "180px",
      selector: (row) => {
        const openings = row.session_capacity - row.session_booking_count;
        return openings > 0 ? (
          openings
        ) : (
          <Status status="Full" color="#FF0000" />
        );
      },
      sortable: true,
    },
    {
      name: "Action",
      width: "150px",
      cell: (row) => {
        const openings = row.session_capacity - row.session_booking_count;
        const isFull = openings <= 0;
        const buttonId = `booking-button-${row.id || row.guid}`; // Unique ID for each button

        return (
          <>
            <button
              id={buttonId}
              onClick={() => handleBookNowClick(row)}
              disabled={row.is_past}
              data-tooltip-id={`tooltip-${buttonId}`}
              data-tooltip-content={row.is_past ? "Booking not available for past sessions" : ""}
              className={`px-4 py-2 rounded transition-colors ${row.is_past
                ? "bg-gray-300 cursor-not-allowed opacity-60"
                : isFull
                  ? "bg-yellow-500 text-white hover:bg-yellow-600" // Waitlist button styling
                  : "bg-primary text-white hover:bg-primary-dark"
                }`}
            >
              {isFull ? "Join Waitlist" : "Book Now"}
            </button>

            {/* Tooltip component */}
            {row.is_past && (
              <Tooltip
                id={`tooltip-${buttonId}`}
                place="top"
                effect="solid"
              />
            )}
          </>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const formStatus = formData?.data?.status === 1;
  if (!formStatus) {
    return (
      <div className="max-w-4xl mx-auto p-5">
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
          <div className="bg-white rounded-lg shadow-md text-center w-10/12 relative">
            <div className="p-8 py-6 p-3 bg-primary rounded-t-lg">
              <div className="text-white w-12 h-12 rounded-full border-4 border-white flex items-center justify-center mx-auto">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-x"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </div>
              <h4 className="text-2xl font-medium text-white mt-2">
                Form Unavailable
              </h4>
            </div>
            <div className="p-8 py-8">
              <p className="text-base font-medium text-gray-500 leading-[24px]">
                This form is currently inactive or has been disabled by the
                administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isClassRegistration =
    formData?.data?.form_type === "class_registration";
  // const isEventRegistration =
  //   formData?.data?.form_type === "event_registration";

  if ((isClassRegistration) && !showForm) {
    return (
      <div className="max-w-4xl mx-auto p-3 sm:p-5">
        {isClassRegistration && (
          <>
            <div className="mb-8">
              <h2 className="text-3xl font-semibold mb-6 text-primary">
                Register for a Class
              </h2>
              <p className="text-gray-600">
                Browse available classes organized by weekday. Select your
                preferred class and secure your spot today.
              </p>
            </div>
            {/* <ClassListing
              classes={allClasses}
              onBookNow={handleBookNow}
              fetchSessionsByDate={handleSessionsByDate}
              userId={userId}
              isLoading={sessionsByDateLoading}
              fetchedDates={fetchedDates}
            /> */}
            {/* Class Listing */}
            {/* Navigation Header */}
            {/* Week Days Selector */}
            <div className="bg-white rounded-lg shadow-[0px_0px_20px_rgba(0,0,0,0.06)]">
              <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-300/50">
                <div className="flex justify-center items-center">
                  <Button
                    size="md"
                    variant="transparent"
                    icon={<SvgIcon name="chevron-left" />}
                    onClick={handlePreviousWeek}
                  />
                  <div className="text-center mx-2">
                    <h2 className="text-sm sm:text-base font-semibold">
                      {format(currentDate, "MMMM yyyy")},{" "}
                      {format(startOfWeek(currentDate), "dd MMM")}-
                      {format(endOfWeek(currentDate), "dd MMM")}
                    </h2>
                  </div>
                  <Button
                    variant="transparent"
                    size="md"
                    icon={<SvgIcon name="chevron-right" />}
                    onClick={handleNextWeek}
                  />
                </div>
              </div>
              <div className="flex justify-between p-2 sm:p-4 py-2 sm:py-3">
                {getDaysInWeek().map((date) => (
                  <div
                    key={date.toString()}
                    onClick={() => handleDateClick(date)}
                    role="button" // Add a button role
                    tabIndex={0} // Make it focusable with the keyboard
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleDateClick(date);
                      }
                    }} // Handle keyboard interaction
                    className={`flex-1 max-w-[140px] cursor-pointer text-center px-1.5 sm:px-3 md:px-4 py-2 sm:py-3 rounded-lg border
                                        ${isSameDay(date, selectedDate)
                        ? "bg-[rgba(var(--primary-rgb)/10%)] text-[rgba(var(--primary-rgb)/100)] border-transparent"
                        : isToday(date)
                          ? "!border-[rgba(var(--primary-rgb)/15%)] text-[rgba(var(--primary-rgb)/100)]"
                          : "border-transparent"
                      }`}
                  >
                    <div className="text-base sm:text-xl font-semibold mb-1">
                      {format(date, "d")}
                    </div>
                    <div className="text-xs sm:text-sm font-medium">
                      {format(date, "EEE")}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Date Classes */}
            {selectedDate && (
              <div className="mt-3">
                {sessionsByDateLoading ? (
                  <FormCenteredLoader color={formPrimaryColor} />
                ) : (
                  <TableCard
                    header={
                      <div className="flex items-center">
                        <div className="flex-auto">
                          <h3 className="text-base sm:text-lg font-medium">
                            Classes for {format(selectedDate, "dd MMMM yyyy")}
                          </h3>
                        </div>
                      </div>
                    }
                  >
                    <DataTable
                      data={allClasses}
                      columns={columns(handleBookNow)}
                    />
                  </TableCard>
                )}
              </div>
            )}
          </>
        )}
        {/* {isEventRegistration && (
          <>
            <div className="mb-8">
              <h2 className="text-3xl font-semibold mb-6 text-primary">
                Register for a Event
              </h2>
              <p className="text-gray-600">
                Browse available events organized by weekday. Select your
                preferred event and secure your spot today.
              </p>
            </div>
            {/* <EventListing
              events={allEvents}
              onBookNow={handleBookNow}
              fetchSessionsByDate={handleSessionsByDate}
              userId={userId}
              isLoading={sessionsByDateLoading}
            /> */}
        {/* <div className="bg-white rounded-lg shadow-[0px_0px_20px_rgba(0,0,0,0.06)]">
              <div className="px-4 py-3 border-b border-gray-300/50">
                <div className="flex justify-center items-center gap-2">
                  <Button
                    size="md"
                    variant="transparent"
                    icon={<SvgIcon name="chevron-left" />}
                    onClick={handlePreviousWeek}
                  />
                  <div className="text-center mx-2">
                    <h2 className="text-xl font-semibold">
                      {format(currentDate, "MMMM yyyy")},{" "}
                      {format(startOfWeek(currentDate), "dd MMM")}-
                      {format(endOfWeek(currentDate), "dd MMM")}
                    </h2>
                  </div>
                  <Button
                    variant="transparent"
                    size="md"
                    icon={<SvgIcon name="chevron-right" />}
                    onClick={handleNextWeek}
                  />
                </div>
              </div> */}
        {/* <div className="flex justify-between p-4 py-3">
                {getDaysInWeek().map((date) => (
                  <div
                    key={date.toString()}
                    onClick={() => handleDateClick(date)}
                    role="button" // Add a button role
                    tabIndex={0} // Make it focusable with the keyboard
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleDateClick(date);
                      }
                    }} // Handle keyboard interaction
                    className={`flex-1 max-w-[140px] cursor-pointer text-center px-4 py-3 rounded-lg border
                                        ${
                                          isSameDay(date, selectedDate)
                                            ? "bg-[rgba(var(--primary-rgb)/10%)] text-[rgba(var(--primary-rgb)/100)] border-transparent"
                                            : isToday(date)
                                            ? "!border-[rgba(var(--primary-rgb)/15%)] text-[rgba(var(--primary-rgb)/100)]"
                                            : "border-transparent"
                                        }`}
                  >
                    <div className="text-xl font-semibold mb-1">
                      {format(date, "d")}
                    </div>
                    <div className="text-sm font-medium">
                      {format(date, "EEEE")}
                    </div>
                  </div>
                ))}
              </div>
            </div> */}

        {/* Selected Date Classes */}
        {/* {selectedDate && (
              <div className="mt-3">
                {sessionsByDateLoading ? (
                  <LoadingState />
                ) : (
                  <TableCard
                    header={
                      <div className="flex items-center">
                        <div className="flex-auto">
                          <h3 className="text-lg sm:text-xl font-medium">
                            Classes for {format(selectedDate, "dd MMMM yyyy")}
                          </h3>
                        </div>
                      </div>
                    }
                  >
                    <DataTable
                      data={allEvents}
                      columns={columns(handleBookNow)}
                    />
                  </TableCard>
                )}
              </div>
            )}
          </> */}
        {/* )}  */}
        {/* } */}

        {/* Delete Component */}
        <DeletePopup
          isOpen={confirmModal.isOpen}
          onClose={() =>
            setConfirmModal({ isOpen: false, message: "", classData: null })
          }
          layout="full"
          title="Booking Confirmation"
          message={confirmModal.message}
          onConfirm={handleConfirmBooking}
          confirmLabel="Confirm"
        />
      </div>
    );
  }

  // Get form styling settings with defaults
  const labelPosition = formData?.data?.label_position || "top";
  const backgroundColor = formData?.data?.background_color || null;
  const labelColor = formData?.data?.label_color || null;
  const titleColor = formData?.data?.title_color || null;
  // Primary = headings + buttons (never label/text color)
  const primaryColor = resolveFormPrimaryColor(themeColorParam, titleColor);
  const fieldSpacing = formData?.data?.field_spacing || "medium";
  const fieldHeight = formData?.data?.field_height || "medium";
  // Only use field_width if explicitly set, otherwise undefined (full width)
  const fieldWidth = formData?.data?.field_width || undefined;
  const fieldAlignment = formData?.data?.field_alignment || "left";
    // Get spacing classes based on fieldSpacing
  const getSpacingClass = () => {
    switch (fieldSpacing) {
      case "small":
        return "gap-2";
      case "large":
        return "gap-6";
      case "medium":
      default:
        return "gap-4";
    }
  };

  const spacingClass = getSpacingClass();

  // Get field width classes based on fieldWidth and fieldAlignment
  // If fieldWidth is undefined/null, return full width (no max-width restriction)
  // Only apply width restrictions when explicitly set
  const getFieldWidthClasses = () => {
    if (!fieldWidth || fieldWidth === undefined || fieldWidth === null) {
      return "w-full"; // Full width when not set (matches container)
    }

    // Get alignment margin class
    const alignmentMargin = fieldAlignment === "center" ? "mx-auto" : "";

    switch (fieldWidth) {
      case "small":
        return `w-full max-w-sm ${alignmentMargin}`;
      case "large":
        return "w-full"; // Full width (matches container width)
      case "medium":
      default:
        return `w-full max-w-xl ${alignmentMargin}`;
    }
  };

  // Get field height classes based on fieldHeight
  const getFieldHeightClasses = () => {
    switch (fieldHeight) {
      case "small":
        return {
          input: "py-1.5 px-3 text-sm min-h-[36px]",
          button: "px-3 py-1.5 text-sm min-h-[36px]",
          checkbox: "min-w-5 min-h-5 w-5 h-5",
        };
      case "large":
        return {
          input: "py-3.5 px-4 text-base min-h-[56px]",
          button: "px-6 py-4 text-base sm:text-lg min-h-[56px]",
          checkbox: "min-w-7 min-h-7 w-7 h-7",
        };
      case "medium":
      default:
        return {
          input: "py-2 sm:py-2.5 px-4 text-sm sm:text-base min-h-[46px]",
          button: "px-4 py-2.5 text-sm sm:text-base min-h-[46px]",
          checkbox: "min-w-6 min-h-6 w-6 h-6",
        };
    }
  };

  // Get form-wide size classes based on fieldHeight (applies to all sections)
  const getFormSizeClasses = (embedded = false) => {
    // Embed (iframe / ?embed=1): strip outer padding so host page controls spacing
    if (embedded) {
      return {
        containerPadding: "pl-5 pr-3 sm:pl-6 sm:pr-4",
        titleSize: "text-xl sm:text-2xl",
        titleMargin: "mb-3",
        descriptionSize: "text-sm",
        sectionSpacing: "pt-3",
        buttonPadding: "px-4 py-2.5 text-sm sm:text-base",
        buttonHeight: "min-h-[46px]",
        checkboxText: "text-sm",
        termsSpacing: "pt-3",
        submitSpacing: "pt-4",
      };
    }

    switch (fieldHeight) {
      case "small":
        return {
          containerPadding: "p-3 sm:p-4",
          titleSize: "text-xl sm:text-2xl",
          titleMargin: "mb-3 sm:mb-4",
          descriptionSize: "text-xs sm:text-sm",
          sectionSpacing: "pt-3",
          buttonPadding: "px-3 py-1.5 text-sm",
          buttonHeight: "min-h-[36px]",
          checkboxText: "text-xs sm:text-sm",
          termsSpacing: "pt-3",
          submitSpacing: "pt-6",
        };
      case "large":
        return {
          containerPadding: "p-4 sm:p-5",
          titleSize: "text-3xl sm:text-4xl",
          titleMargin: "mb-4 sm:mb-5",
          descriptionSize: "text-base sm:text-lg",
          sectionSpacing: "pt-5",
          buttonPadding: "px-6 py-4 text-base sm:text-lg",
          buttonHeight: "min-h-[56px]",
          checkboxText: "text-base sm:text-lg",
          termsSpacing: "pt-5",
          submitSpacing: "pt-8",
        };
      case "medium":
      default:
        return {
          containerPadding: "p-3 sm:p-4",
          titleSize: "text-2xl sm:text-3xl",
          titleMargin: "mb-4 sm:mb-5",
          descriptionSize: "text-sm sm:text-base",
          sectionSpacing: "pt-4",
          buttonPadding: "px-4 py-2.5 text-sm sm:text-base",
          buttonHeight: "min-h-[46px]",
          checkboxText: "text-sm sm:text-base",
          termsSpacing: "pt-4",
          submitSpacing: "pt-6",
        };
    }
  };

  // Get text alignment classes for titles and text elements
  const getTextAlignmentClasses = () => {
    switch (fieldAlignment) {
      case "center":
        return "text-center";
      case "left":
      default:
        return "text-left"; // Default is left-aligned
    }
  };

  // Get alignment classes for field containers (overrides mx-auto from width classes)
  const getFieldAlignmentClasses = () => {
    switch (fieldAlignment) {
      case "center":
        return "mx-auto";
      case "left":
      default:
        return ""; // Default is left-aligned, no margin needed
    }
  };

  const isEmbedded = isEmbedQuery || isInIframe;
  const formSizeClasses = getFormSizeClasses(isEmbedded);
  const fieldWidthClass = isEmbedded ? "w-full" : getFieldWidthClasses();
  const fieldHeightClasses = getFieldHeightClasses();
  const textAlignmentClasses = getTextAlignmentClasses();
  const fieldAlignmentClasses = getFieldAlignmentClasses();

  // Helper function to generate placeholder text based on field label and type
  // Only returns placeholder when labelPosition is "placeholder"
  const getPlaceholder = (field) => {
    // Only show placeholder when label position is "placeholder"
    if (labelPosition !== "placeholder") {
      return "";
    }

    const fieldType = field?.field_type?.toLowerCase() || '';

    // Priority 1: Use placeholder from database if available
    if (field?.placeholder && field.placeholder.trim() !== '') {
      return field.placeholder.trim();
    }

    // Priority 2: Generate simple placeholder format

    // For select/dropdown fields - use "Select [field name]" or just "[field name]"
    if (fieldType === 'select') {
      const fieldLabel = field?.label || '';
      if (fieldLabel) {
        const cleanLabel = fieldLabel
          .replace(/[^\w\s]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        // For location field, show "Location" or "Select location" 
        // For other fields, show "Select [field name]"
        const lowerLabel = cleanLabel.toLowerCase();
        if (lowerLabel === 'location' || field?.slug === 'location') {
          return 'Select location';
        }
        return `Select ${cleanLabel.toLowerCase()}`;
      }
      // Fallback: if no label but it's a location field by slug
      if (field?.slug === 'location') {
        return 'Select location';
      }
      return 'Select...';
    }

    // For date fields - use "Select..."
    if (fieldType === 'date') {
      const fieldLabel = field?.label || '';
      if (fieldLabel) {
        const cleanLabel = fieldLabel
          .replace(/[^\w\s]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
        return `Select ${cleanLabel}`;
      }
      return 'Select date';
    }

    // For all other fields (text, email, phone, textarea, number, etc.) - use "Enter [field name]"
    const fieldLabel = field?.label || '';
    if (fieldLabel) {
      // Convert label to lowercase and clean it
      const cleanLabel = fieldLabel
        .replace(/[^\w\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      return `Enter ${cleanLabel}`;
    }

    // Final fallback
    return 'Enter value';
  };

  const renderField = (field) => {
    if (field?.is_active === 0) {
      return null; // Do not render inactive fields
    }
    // Use slug as key for dynamic fields to ensure uniqueness, fallback to id for static fields
    const fieldKey = field?.slug ? `field-${field.slug}` : `field-${field.id}`;

    // Determine width class based on field width percentage - same on all devices
    const getWidthClass = (width) => {
      if (!width) { return 'w-full'; }

      // Remove % symbol and convert to number
      const widthValue = parseInt(width.replace('%', ''));

      switch (widthValue) {
        case 25: return 'w-1/4';
        case 33: return 'w-1/3';
        case 50: return 'w-1/2';
        case 66: return 'w-2/3';
        case 75: return 'w-3/4';
        case 100:
        default: return 'w-full';
      }
    };

    const widthClass = getWidthClass(field?.width);

    if (isSignatureField(field)) {
      return (
        <div className={widthClass}>
          <SignatureField
            key={fieldKey}
            label={field?.label}
            name={field?.slug}
            register={register}
            setValue={setValue}
            clearErrors={clearErrors}
            trigger={trigger}
            errors={errors}
            existingValue={watch(field?.slug)}
            metadataFieldName={`${field?.slug}_metadata`}
            onSignatureRef={(ref) => {
              signatureRefs.current[field.slug] = ref;
            }}
            labelPosition={labelPosition}
            fieldHeight={fieldHeight}
            fieldWidth={fieldWidth}
            fieldAlignment={fieldAlignment}
            labelColor={labelColor || undefined}
          />
        </div>
      );
    }
    //test
    switch (field?.field_type) {
      case "radio":
        const radioLabelClass = labelPosition === "left"
          ? "block text-sm sm:text-base whitespace-nowrap min-w-[120px] mb-0"
          : `block text-sm sm:text-base mb-2 sm:mb-2.5 ${fieldAlignment === "center" ? "text-center" : ""}`;
        const radioLabelStyle = labelColor ? { color: labelColor } : { color: '#374151' }; // default text-gray-700
        const radioContainerClass = labelPosition === "left"
          ? "flex items-start gap-3"
          : fieldAlignment === "center"
            ? "flex flex-col items-center gap-2"
            : "flex flex-col gap-2";
        return (
          <div className={`${widthClass} ${fieldAlignment === "center" ? fieldAlignmentClasses : ""}`}>
            <div className={radioContainerClass}>
              <label className={radioLabelClass} style={radioLabelStyle}>{field?.label}</label>
              <div className={labelPosition === "left" ? "flex-1 min-w-0 flex flex-col gap-2" : fieldAlignment === "center" ? "flex flex-col items-center justify-center gap-2 w-full" : "flex flex-col gap-2"}>
                {(() => {
                  const options =
                    typeof field?.options === "string"
                      ? JSON.parse(field.options)
                      : field?.options || [];

                  return (
                    <>
                      {options.length > 0 &&
                        options.map((option, index) => (
                          <div key={`${fieldKey}-option-wrapper-${index}`} className={fieldAlignment === "center" ? "flex justify-center" : ""}>
                            <Radio
                              key={`${fieldKey}-option-${index}`}
                              label={option}
                              id={`${field?.slug}-${index}`}
                              value={option}
                              {...register(field?.slug)}
                              setValue={setValue}
                            />
                          </div>
                        ))}
                      {errors[field?.slug] && (
                        <span className={`text-red-600 ${fieldAlignment === "center" ? "text-center block w-full" : ""}`}>
                          {errors[field?.slug]?.message}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );

      case "checkbox":
        const checkboxLabelClass = labelPosition === "left"
          ? "block text-sm sm:text-base whitespace-nowrap min-w-[120px] mb-0"
          : `block text-sm sm:text-base mb-2 sm:mb-2.5 ${fieldAlignment === "center" ? "text-center" : ""}`;
        const checkboxLabelStyle = labelColor ? { color: labelColor } : { color: '#374151' }; // default text-gray-700
        const checkboxContainerClass = labelPosition === "left"
          ? "flex items-start gap-3"
          : fieldAlignment === "center"
            ? "flex flex-col items-center gap-2"
            : "flex flex-col gap-2";
        return (
          <div className={`${widthClass} ${fieldAlignment === "center" ? fieldAlignmentClasses : ""}`}>
            <div className={checkboxContainerClass}>
              <label className={checkboxLabelClass} style={checkboxLabelStyle}>{field?.label}</label>
              <div className={labelPosition === "left" ? "flex-1 min-w-0 flex flex-col gap-2" : fieldAlignment === "center" ? "flex flex-col items-center justify-center gap-2 w-full" : "flex flex-col gap-2"}>
                {(() => {
                  const options =
                    typeof field.options === "string"
                      ? JSON.parse(field.options)
                      : field.options || [];

                  return (
                    <>
                      {options.map((option, optionIndex) => (
                        <div key={`${fieldKey}-option-wrapper-${optionIndex}`} className={fieldAlignment === "center" ? "flex justify-center w-full" : ""}>
                          <Checkbox
                            key={`${fieldKey}-option-${optionIndex}`}
                            label={option}
                            name={`${field?.slug}[${optionIndex}]`}
                            watch={watch}
                            setValue={setValue}
                            register={(name) =>
                              register(field?.slug, {
                                setValueAs: (value) =>
                                  !value ? [] : Array.isArray(value) ? value : [value], // Simplified arrow function
                              })
                            }
                            className="form-checkbox"
                          />
                        </div>
                      ))}
                      {/* Display error message outside the checkbox group */}
                      {errors[field?.slug] && (
                        <div className={`text-red-600 text-sm mt-1 ${fieldAlignment === "center" ? "text-center w-full" : ""}`}>
                          {errors[field?.slug]?.message}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );

      case "file":
        const fileLabelClass = labelPosition === "left"
          ? "block text-sm sm:text-base whitespace-nowrap min-w-[120px] mb-0"
          : "";
        const fileLabelStyle = labelColor ? { color: labelColor } : { color: '#374151' }; // default text-gray-700
        const fileContainerClass = labelPosition === "left"
          ? "flex items-start gap-3"
          : fieldAlignment === "center"
            ? "flex flex-col items-center text-center [&>div>label]:text-center [&>div>div]:justify-center"
            : "";
        return (
          <div className={`${widthClass} ${fieldAlignment === "center" ? fieldAlignmentClasses : ""}`}>
            <div className={fileContainerClass}>
              {labelPosition === "left" && (
                <label className={fileLabelClass} style={fileLabelStyle}>{field?.label}</label>
              )}
              <div className={labelPosition === "left" ? "flex-1 min-w-0" : ""}>
                <FileInputImages
                  key={fieldKey}
                  label={labelPosition === "left" ? "" : field?.label}
                  accept="image/*,.pdf,.doc,.docx"
                  isMulti="false"
                  customType="allFile"
                  name={field?.slug}
                  register={register}
                  clearErrors={clearErrors}
                  existingMedia={existingMedia}
                  errors={errors}
                  buttonText={`Select ${field?.label}`}
                  setValue={setValue}
                  labelColor={labelColor || undefined}
                  labelPosition={labelPosition}
                  fieldHeight={fieldHeight}
                />
              </div>
            </div>
          </div>
        );

      case "textarea":
        return (
          <InputField
            key={fieldKey}
            multiline="true"
            float="false"
            control={control}
            name={field?.slug}
            label={field?.label}
            register={register}
            errors={errors}
            setValue={setValue}
            placeholder={getPlaceholder(field)}
            labelPosition={labelPosition}
            fieldHeight={fieldHeight}
            fieldWidth={fieldWidth}
            fieldAlignment={fieldAlignment}
            labelColor={labelColor}
          />
        );

      case "phone":
        return (
          <PhoneInputField
            key={fieldKey}
            control={control}
            float="false"
            name={field?.slug}
            label={field?.label}
            register={register}
            clearErrors={clearErrors}
            errors={errors}
            setValue={setValue}
            placeholder={getPlaceholder(field)}
            labelPosition={labelPosition}
            fieldHeight={fieldHeight}
            fieldWidth={fieldWidth}
            fieldAlignment={fieldAlignment}
            labelColor={labelColor}
          />
        );

      case "select": {
        // Parse the field's own options, not modifiedSessionData
        const selectOptions = (() => {
          let options = [];
          if (field?.options) {
            try {
              options = typeof field.options === "string"
                ? JSON.parse(field.options)
                : field.options;
            } catch (e) {
                            options = [];
            }
          }
          // Transform options to the format expected by DropdownField
          return options.map(option => ({
            label: option,
            value: option
          }));
        })();

        const isMultiSelect = field?.is_multi_select || field?.multiple || field?.slug === "interested_in";

        return (
          <DropdownField
            key={fieldKey}
            control={control}
            isMulti={isMultiSelect}
            label={field?.label}
            name={field?.slug}
            options={
              field?.slug === "interested_in"
                ? modifiedSessionData
                : field?.slug === "location"
                  ? modifiedLocationData
                  : selectOptions
            }
            register={register}
            errors={errors}
            setValue={setValue}
            clearErrors={clearErrors}
            defaultValue={isMultiSelect ? [] : ""}
            placeholder={getPlaceholder(field)}
            labelPosition={labelPosition}
            fieldHeight={fieldHeight}
            fieldWidth={fieldWidth}
            fieldAlignment={fieldAlignment}
            labelColor={labelColor}
          />
        );
      }

      default: {
        const isDateField = field?.field_type === "date";
        const isDobField = [
          "dob",
          "date_of_birth",
          "child_1_dob",
          "child_2_dob",
          "child_3_dob",
          "child_4_dob",
          "child_5_dob"
        ].includes(field?.slug?.toLowerCase());

        const minDate = new Date(
          new Date().getFullYear() - 100,
          new Date().getMonth(),
          new Date().getDate()
        )
          .toISOString()
          .split("T")[0];

        const maxDate = new Date().toISOString().split("T")[0];

        return (
          <InputField
            key={fieldKey}
            float="false"
            control={control}
            type={field?.field_type}
            name={field?.slug}
            label={field?.label}
            register={register}
            errors={errors}
            setValue={setValue}
            placeholder={getPlaceholder(field)}
            labelPosition={isDateField ? (labelPosition === "placeholder" ? "top" : labelPosition) : labelPosition}
            fieldHeight={fieldHeight}
            fieldWidth={fieldWidth}
            fieldAlignment={fieldAlignment}
            labelColor={labelColor}
            {...(isDateField && isDobField && {
              max: maxDate,
              min: minDate,
            })}
          />
        );
      }
    }
  };

  // Use form background_color when set; otherwise white (never leave gray body showing through)
  const resolvedBackgroundColor = backgroundColor || "#ffffff";
  const backgroundStyle = { backgroundColor: resolvedBackgroundColor };

  return (
    <div className="relative" data-form-embed-root style={backgroundStyle}>
      {/* Centered overlay loader for form submission */}
      {isSubmitting && <FormCenteredLoader color={primaryColor} />}
      <div
        className={`${isEmbedded ? "w-full max-w-none mx-0" : "max-w-4xl mx-auto"} ${formSizeClasses.containerPadding}`}
        style={backgroundStyle}
      >
        {formData?.data?.form_type === "class_registration" && (
          /* Back button */
          <div className="mb-4">
            <button
              className="inline-flex font-medium"
              onClick={() => setShowForm(false)}
              style={primaryColor ? { color: primaryColor } : undefined}
            >
              <span className="inline-block mr-2">
                <SvgIcon name="arrow-left" />
              </span>
              Back to Sessions
            </button>
          </div>
        )}
        <div className={`w-full ${formSizeClasses.titleMargin}`}>
          {logoUrl && (
            <div className="mb-4 flex">
              <img
                src={logoUrl}
                alt="Logo"
                className="h-16 object-contain"
              />
            </div>
          )}
          {formData?.data?.title && (formData?.data?.title_status === undefined || formData?.data?.title_status === 1) && (
            <h2
              className={`${formSizeClasses.titleSize} font-semibold ${textAlignmentClasses}`}
              style={primaryColor ? { color: primaryColor } : { color: PORTAL_PRIMARY_COLOR }}
            >
              {formData?.data?.title}
            </h2>
          )}

          {formData?.data?.event_guid && formData?.data?.event?.length > 0 && (
            <h4 className={`${formSizeClasses.descriptionSize} font-semibold mt-3`}>
              Event: {formData?.data?.event[0]?.name}
            </h4>
          )}

          {formData?.data?.classes_guid && formData?.data?.class?.length > 0 && (
            <div>
              <h4 className={`${formSizeClasses.descriptionSize} font-semibold mt-3`}>
                Class: {formData?.data?.class[0]?.name}
              </h4>
            </div>
          )}
          {formData?.data?.description && (formData?.data?.description_status === undefined || formData?.data?.description_status === 1) && (
            <p className={`${formSizeClasses.descriptionSize} font-medium text-gray-600 leading-[normal] mt-2`}>
              {formData?.data?.description}
            </p>
          )}
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className={`grid grid-cols-1 ${spacingClass}`}>
            {/* Render non-child fields first (excluding signature fields) */}
            {formData?.data?.form_question
              ?.filter((field) =>
                field?.is_active !== 0 &&
                !field.field_category?.startsWith("child_") &&
                !isSignatureField(field)
              )
              .map((field) => renderField(field))}

            {/* Render child fields grouped (excluding signature fields) */}
            {Object.entries(groupChildFields(formData?.data?.form_question))
              .sort()
              .map(([childCategory, fields]) => (
                <div key={childCategory} className={formSizeClasses.sectionSpacing}>
                  <h3
                    className={`${formSizeClasses.descriptionSize} font-semibold mb-4 capitalize ${textAlignmentClasses}`}
                    style={labelColor ? { color: labelColor } : { color: '#374151' }}
                  >
                    {childCategory.replace("_", " ")}
                  </h3>
                  <div className={`grid grid-cols-1 ${spacingClass}`}>
                    {" "}
                    {/* Modified to single column */}
                    {fields
                      .filter((field) => !isSignatureField(field))
                      .sort((a, b) => {
                        const order = ["first_name", "last_name", "dob"];
                        // Extract the base field name (e.g., "first_name" from "child_1_first_name")
                        const aBase = a.slug.replace(/^child_\d+_/, "");
                        const bBase = b.slug.replace(/^child_\d+_/, "");
                        return (
                          order.indexOf(aBase) -
                          order.indexOf(bBase)
                        );
                      })
                      .map((field) => renderField(field))}
                  </div>
                </div>
              ))}

            {/* Render dynamically added children (only for client_registration) */}
            {isClientRegistration && dynamicChildren.map((childNumber) => {
              const dynamicFields = createDynamicChildFields(childNumber);
              if (dynamicFields.length === 0) {
                return null;
              }
              return (
                <div key={`dynamic-child-${childNumber}`} className={formSizeClasses.sectionSpacing}>
                  <div className={`flex items-center gap-2 mb-4 ${fieldWidthClass} justify-between`}>
                    <h3
                      className={`${formSizeClasses.descriptionSize} font-semibold capitalize ${textAlignmentClasses}`}
                      style={labelColor ? { color: labelColor } : { color: '#374151' }}
                    >
                      Child {childNumber}
                    </h3>
                    <Button
                      variant="transparent"
                      size="md"
                      icon={<SvgIcon name="delete" size="1em" />}
                      className="bg-red-600/15 text-red-600"
                      onClick={() => handleRemoveChild(childNumber)}
                    />
                  </div>
                  <div className={`grid grid-cols-1 ${spacingClass}`}>
                    {dynamicFields
                      .filter((field) => !isSignatureField(field))
                      .sort((a, b) => {
                        const order = ["first_name", "last_name", "dob"];
                        // Extract the base field name (e.g., "first_name" from "child_2_first_name")
                        const aBase = a.slug.replace(/^child_\d+_/, "");
                        const bBase = b.slug.replace(/^child_\d+_/, "");
                        return (
                          order.indexOf(aBase) -
                          order.indexOf(bBase)
                        );
                      })
                      .map((field) => (
                        <React.Fragment key={field.slug}>
                          {renderField(field)}
                        </React.Fragment>
                      ))}
                  </div>
                </div>
              );
            })}

            {/* Add Child Button (only for client_registration and when child template exists) */}
            {isClientRegistration && getChildFieldTemplate.length > 0 && (
              <div className={formSizeClasses.sectionSpacing}>
                <button
                  type="button"
                  onClick={handleAddChild}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors ${formSizeClasses.buttonHeight}`}
                  style={
                    primaryColor
                      ? { backgroundColor: primaryColor, color: "#ffffff" }
                      : undefined
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Add Child
                </button>
              </div>
            )}

            {/* Always render signature fields at the bottom after all other fields */}
            {formData?.data?.form_question
              ?.filter((field) =>
                field?.is_active !== 0 &&
                isSignatureField(field)
              )
              .map((field) => renderField(field))}
          </div>
          <div className={`${formSizeClasses.termsSpacing} ${fieldWidthClass}`}>
            <Checkbox
              name="terms_&_conditions"
              register={register}
              watch={watch}
              setValue={setValue}
              className="form-checkbox"
              label={
                <>
                  <span className={formSizeClasses.checkboxText}>
                    By submitting this form you agree to the{" "}
                    <a
                      href={
                        process.env.NEXT_PUBLIC_MEDIA_BASE_URL
                          ? `${String(process.env.NEXT_PUBLIC_MEDIA_BASE_URL).trim().replace(/\/$/, "")}/uploads/terms/terms.pdf`
                          : "/terms/terms.pdf"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Gymboree Play & Music terms & conditions
                    </a>
                  </span>
                </>
              }
              fieldHeight={fieldHeight}
            />
            {errors["terms_&_conditions"] && (
              <p className={`text-red-500 ${formSizeClasses.checkboxText} mt-1`}>
                {errors["terms_&_conditions"].message}
              </p>
            )}

          </div>
          <div className={`${formSizeClasses.submitSpacing} ${fieldWidthClass} ${fieldAlignmentClasses}`}>
            <Button
              label="Submit"
              variant="solid"
              type="submit"
              className={`w-full ${formSizeClasses.buttonPadding} ${formSizeClasses.buttonHeight}${primaryColor ? " !text-white" : ""}`}
              style={
                primaryColor
                  ? {
                      backgroundColor: primaryColor,
                      borderColor: primaryColor,
                      color: "#ffffff",
                    }
                  : undefined
              }
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default function CustomerFormPageWithSuspense() {
  return (
    <Suspense fallback={<FormCenteredLoader />}>
      <CustomerFormPage />
    </Suspense>
  );
}
